/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import Bio from 'src/components/PageTitle/Bio'
import Text from 'src/components/ts-components/Text'
import { PageTitle } from '../steps/Main'
import { SelectInput } from 'src/components/ts-components/SelectInput'
import {
  NEW_IDENTITY_ID,
  buildIdentityOptions,
  findIdentityPerson,
  hasIdentityStats,
  identityLabel,
  identityPersonId,
  identitySubtitle,
  type IdentityOptionItem,
  type IdentityPerson,
} from '../utils/identityOptions'

const PLAYER_PLACEHOLDER = '/images/generic_placeholders/placeholder_player.png'

interface Props {
  /**
   * The people this reader can register — their family when registering a
   * player, their team officials when registering a team. The caller picks the
   * list; this only renders it.
   */
  people: IdentityPerson[] | null | undefined
  /** Registering a team rather than a player, which changes the wording. */
  isTeamRole: boolean
  /** The person currently chosen, or the create-new sentinel. */
  selected: IdentityPerson | null | undefined
  /** Passed the chosen person, or null for "create new". */
  onSelect: (person: IdentityPerson | null) => void
  disabled?: boolean
}

const createNewLabel = (isTeamRole: boolean): string =>
  isTeamRole ? 'Create a New Team Official' : 'Create a New Player'

/**
 * The text a typed query is matched against for one option.
 *
 * The create-new row keeps its own label; everyone else matches on the
 * searchable string `buildIdentityOptions` built, which carries the email too.
 */
const searchTextFor = (
  option: { value?: unknown } | undefined,
  people: IdentityPerson[] | null | undefined
): string => {
  const value = String(option?.value ?? '')
  if (!value || value === NEW_IDENTITY_ID) {
    return value === NEW_IDENTITY_ID ? 'create new' : ''
  }
  const person = findIdentityPerson(people, value)
  return person ? `${identityLabel(person)} ${identitySubtitle(person)}` : ''
}

/**
 * One row: avatar, name, then whatever identifies the person — the stat icons
 * and their email.
 *
 * The stat line is the shared `Bio` — the same age/height/weight icon row the
 * player header uses — rather than a second implementation of it here. Both the
 * stats and the email are optional and render independently, so a row shows
 * what it has and never leaves a blank line.
 */
const renderPersonRow = (person: IdentityPerson) => {
  const showStats = hasIdentityStats(person)
  const subtitle = identitySubtitle(person)
  return (
    <div css={rowStyle}>
      <img
        src={getImageThumbnailId(person) || PLAYER_PLACEHOLDER}
        alt=''
        css={avatarStyle}
        onError={(e) => {
          const img = e.target as HTMLImageElement
          img.onerror = null
          img.src = PLAYER_PLACEHOLDER
        }}
      />
      <span css={rowTextStyle}>
        <Text variant='paragraphSmall' weight='semiBold' color={colors.WHITE}>
          {identityLabel(person)}
        </Text>
        {showStats || subtitle ? (
          // One detail line: the stat icons, then the email after them. Kept on
          // a single row so a person with both does not push the option to
          // three lines; it wraps rather than clipping when there is no space.
          <span css={detailStyle}>
            {showStats ? (
              // Bio's own top margin is sized for a page header; inside a
              // dropdown row the lines sit together.
              <span css={statsStyle}>
                <Bio
                  birthDate={person.birth_date}
                  height={person.height}
                  weight={person.weight}
                />
              </span>
            ) : null}
            {subtitle ? (
              <span css={emailStyle}>
                {/* Separates the email from the stats before it. Only when
                    there ARE stats — a leading dot on a person who has none
                    would read as a missing value. Decorative, so it is not
                    announced; on the span because `Text` takes a closed prop
                    set and would drop an aria-hidden passed to it. */}
                {showStats ? (
                  <span aria-hidden='true' css={dotStyle}>
                    ·
                  </span>
                ) : null}
                <Text
                  variant='paragraphXSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                >
                  {subtitle}
                </Text>
              </span>
            ) : null}
          </span>
        ) : null}
      </span>
    </div>
  )
}

/**
 * Who is being registered, asked beside the form that describes them.
 *
 * This used to be a whole screen ahead of the form, which meant choosing a
 * person before seeing any of what registering them involved. Here, choosing
 * someone prefills the form's reserved fields directly below, so the choice and
 * its consequence are visible together.
 *
 * A dropdown rather than the start step's card grid: it sits above a long form
 * as one field among several, and a grid of cards there would read as the
 * page's subject rather than one of its inputs.
 */
const IdentitySection = ({
  people,
  isTeamRole,
  selected,
  onSelect,
  disabled = false,
}: Props) => {
  // Two lines per row — name above, email/birth date below — so two people
  // sharing a name are distinguishable. Matches the roster add-player search.
  // `label` stays the plain searchable string underneath; only the display is
  // replaced, so antd still matches a typed email against it.
  const options = buildIdentityOptions(people, isTeamRole).map(
    (option: IdentityOptionItem) => ({
      ...option,
      label: option.person ? (
        renderPersonRow(option.person)
      ) : (
        // The create-new row is an action, not a person — colored to read as
        // one rather than as another name in the list.
        <Text variant='paragraphSmall' weight='semiBold' color={colors.PRIMARY}>
          {option.label}
        </Text>
      ),
    })
  )

  // A chosen person shows their own id; the create-new sentinel shows itself.
  // Empty means nothing chosen yet, which renders the placeholder.
  const value = selected ? identityPersonId(selected) ?? NEW_IDENTITY_ID : ''

  return (
    <div css={containerStyle}>
      {/* The shared heading the old select-role screen used, so this section
          reads as the same question it always was rather than a new one. It
          carries the rule beneath the subtitle too. */}
      <PageTitle
        title={
          isTeamRole
            ? 'Select One of Your Team Officials'
            : 'Select One of Your Players'
        }
        subtitle={
          isTeamRole
            ? 'Choose the team official that you want to register or create a new one.'
            : 'Choose the player that you want to register or create a new one.'
        }
        showAlert={false}
        showSuccess={false}
        // The rule goes below the field instead, so it closes off the whole
        // question — heading, subtitle and answer — rather than separating the
        // heading from the field it introduces.
        showDivider={false}
      />

      <div css={fieldStyle}>
        <SelectInput
          label=''
          name='identity'
          value={value}
          disabled={disabled}
          options={options}
          fieldErrors={[]}
          customError=''
          showSearch
          // Match against the option's own searchable text, not its rendered
          // node — the default matcher would stringify the JSX and find
          // nothing. `buildIdentityOptions` puts name AND email in there.
          filterOption={(input, option) =>
            searchTextFor(option, people)
              .toLowerCase()
              .includes(input.trim().toLowerCase())
          }
          popupMatchSelectWidth={false}
          // The chosen row collapses to the name alone; the two-line form
          // belongs in the list, not in the closed field.
          labelRender={({ value: selectedValue }) => {
            const person = findIdentityPerson(people, String(selectedValue))
            return person ? identityLabel(person) : createNewLabel(isTeamRole)
          }}
          placeholder={capitalizeWords(
            isTeamRole ? 'Search Team Officials' : 'Search Players'
          )}
          onChange={(next) =>
            // The sentinel resolves to null: there is no person yet, and the
            // form below collects their details instead.
            onSelect(findIdentityPerson(people, next))
          }
        />
      </div>

      <div css={dividerStyle} />
    </div>
  )
}

export default IdentitySection

/* Closes the section off beneath the field, matching PageTitle's own rule —
   the same token, so the two are indistinguishable where both appear.

   32px, not 24px: PageTitle's fixed 24px bottom margin carries the subtitle's
   line-height leading with it, so an equal 24px here reads as less space than
   the gap above the field. The extra makes the field sit optically centred
   between the subtitle and the rule. */
const dividerStyle = css`
  width: 100%;
  height: 1px;
  margin-top: 32px;
  background-color: ${colors.VERY_DARK_GRAYISH_BLUE};
`

/* PageTitle brings its own vertical margins, so this only stacks. */
const containerStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`

/* The avatar sits at the top rather than centred: the text beside it is one to
   three lines depending on what the person has, and a centred avatar drifts
   down as those lines are added. */
const rowStyle = css`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
  padding: 4px 0;
`

const avatarStyle = css`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  flex: none;
  object-fit: cover;
`

/* Stats and email share one line. `align-items: center` puts the email on the
   icons' optical centre rather than their baseline, which sits low next to a
   16px glyph. */
const detailStyle = css`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 12px;
  min-width: 0;
`

/* Sized directly rather than through a `Text` preset: at paragraphXSmall the
   separator all but disappears between a 16px icon and the email. The
   line-height keeps the taller glyph from stretching the row. */
const dotStyle = css`
  font-size: 20px;
  line-height: 1;
  color: ${colors.NEUTRAL_100};
`

/* The dot sits tight against the email it introduces, closer than the 12px
   between the stats themselves. */
const emailStyle = css`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`

/* Bio is a page-header component and sets `margin-top: 16px`; a dropdown row
   has no room for that. Its gaps and icon sizing are left alone. */
const statsStyle = css`
  > div {
    margin-top: 0;
    gap: 12px;
  }
`

/* The two lines stack; without this they would become two more items in the
   row's own flex line. */
const rowTextStyle = css`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

/* Half width, matching the team select in `TeamInfoSection` directly below —
   the two are the same kind of field and should not disagree on size. Full
   width on mobile, where half a column is unusable. */
const fieldStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 50%;

  @media (max-width: 768px) {
    width: 100%;
  }
`
